import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type ClipboardEvent as ReactClipboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  Filter,
  ArrowUp,
  ArrowDown,
  GripVertical,
} from 'lucide-react'
import {
  ExcelFilterPopover,
  isColumnFilterActive,
  type FilterState,
  type SortItem,
} from './ExcelEditableTable'
import {
  normalizeColumns,
  renameColumn,
  moveColumn,
  moveRow,
  sumColumn,
  parseNumberVi,
  isCoreColumn,
  computeVisibleFreeRows,
  applyPasteToRows,
  copySelectionToTsv,
  clearCellsInSelection,
  dispatchMenuAction,
  type FreeColumn,
  type FreeRow,
} from './freeSheetModel'
import { formatSheetAmount } from '@/lib/sheetAmount'

export type { FreeColumn, FreeRow }

export interface FreeSheetTableProps {
  columns: FreeColumn[]
  rows: FreeRow[]
  onChange: (next: { columns: FreeColumn[]; rows: FreeRow[] }) => void
  readOnly?: boolean
  totalKey?: string
  hideFooter?: boolean
  computeRow?: (row: FreeRow) => FreeRow
  className?: string
}

export const formatMoney = (n: number): string => {
  if (!Number.isFinite(n)) return ''
  return formatSheetAmount(n)
}

export function FreeSheetTable({
  columns: inputColumns,
  rows,
  onChange,
  readOnly = false,
  totalKey,
  hideFooter = false,
  computeRow,
  className = '',
}: FreeSheetTableProps) {
  // Normalize columns: ensure all 6 core columns exist, note is strictly last
  const normalizedCols = useMemo(() => normalizeColumns(inputColumns), [inputColumns])

  // Sorting & Filtering
  const [sort, setSort] = useState<SortItem[]>([])
  const [filters, setFilters] = useState<FilterState>({})
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null)
  const [filterAnchorRect, setFilterAnchorRect] = useState<DOMRect | null>(null)

  // Selection & Range
  const [selected, setSelected] = useState<{ r: number; c: number }>({ r: 0, c: 0 })
  const [anchor, setAnchor] = useState<{ r: number; c: number }>({ r: 0, c: 0 })
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  // Direct Header Renaming
  const [selectedHeaderCol, setSelectedHeaderCol] = useState<string | null>(null)
  const [editingHeaderCol, setEditingHeaderCol] = useState<string | null>(null)
  const [headerDraft, setHeaderDraft] = useState('')
  const headerInputRef = useRef<HTMLInputElement>(null)

  // Column and Row Resizing
  const [colWidths, setColWidths] = useState<Record<string, number>>({})
  const [resizingCol, setResizingCol] = useState<{ colIndex: number; startX: number; startWidth: number } | null>(null)
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({})
  const [resizingRow, setResizingRow] = useState<{ rowId: string; startY: number; startHeight: number } | null>(null)

  // Active editing cell (prevents premature re-formatting while typing large numbers)
  const [editingCell, setEditingCell] = useState<{ r: number; c: number; draft: string } | null>(null)

  // Undo / Redo stacks
  const [history, setHistory] = useState<Array<{ columns: FreeColumn[]; rows: FreeRow[] }>>([])
  const [future, setFuture] = useState<Array<{ columns: FreeColumn[]; rows: FreeRow[] }>>([])

  // Context menu
  const [menu, setMenu] = useState<{
    r: number
    c: number
    x: number
    y: number
  } | null>(null)

  // Rename column dialog state (test / prompt fallback)
  const [renameModal, setRenameModal] = useState<{ key: string; label: string } | null>(null)
  const [renameDraft, setRenameDraft] = useState('')

  // Drag and drop refs and states
  const draggedColIdx = useRef<number | null>(null)
  const draggedRowOrigIdx = useRef<number | null>(null)
  const [dragOverCol, setDragOverCol] = useState<number | null>(null)
  const [dragOverRow, setDragOverRow] = useState<number | null>(null)

  // Input refs for keyboard navigation
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const containerRef = useRef<HTMLDivElement>(null)

  // Compute visible rows mapping to original indices
  const visibleRows = useMemo(
    () =>
      computeVisibleFreeRows({
        rows,
        columns: normalizedCols,
        filters,
        sort,
        computeRow,
      }),
    [rows, normalizedCols, filters, sort, computeRow],
  )

  // Commit changes to history & trigger onChange
  const commit = useCallback(
    (nextRows: FreeRow[], nextCols: FreeColumn[] = normalizedCols) => {
      setHistory((prev) => [...prev.slice(-49), { columns: normalizedCols, rows }])
      setFuture([])
      onChange({ columns: normalizeColumns(nextCols), rows: nextRows })
    },
    [normalizedCols, onChange, rows],
  )

  // Range bounds
  const rMin = Math.min(anchor.r, selected.r)
  const rMax = Math.max(anchor.r, selected.r)
  const cMin = Math.min(anchor.c, selected.c)
  const cMax = Math.max(anchor.c, selected.c)

  const isInRange = (r: number, c: number): boolean => {
    return r >= rMin && r <= rMax && c >= cMin && c <= cMax
  }

  // Focus cell input helper
  const focusCell = useCallback(
    (r: number, c: number) => {
      const safeR = Math.max(0, Math.min(visibleRows.length - 1, r))
      const safeC = Math.max(0, Math.min(normalizedCols.length - 1, c))
      setSelected({ r: safeR, c: safeC })
      setAnchor({ r: safeR, c: safeC })
      const key = `${safeR}_${safeC}`
      const el = inputRefs.current[key]
      if (el) {
        el.focus()
        el.select()
      }
    },
    [normalizedCols.length, visibleRows.length],
  )

  // Mouse selection listeners
  useEffect(() => {
    if (!isSelecting || typeof window === 'undefined') return
    const onMouseUp = () => setIsSelecting(false)
    window.addEventListener('mouseup', onMouseUp)
    return () => window.removeEventListener('mouseup', onMouseUp)
  }, [isSelecting])

  // Column resizing mouse event listeners
  useEffect(() => {
    if (!resizingCol || typeof window === 'undefined') return
    const onMouseMove = (e: MouseEvent) => {
      const col = normalizedCols[resizingCol.colIndex]
      if (!col) return
      const diff = e.clientX - resizingCol.startX
      const newWidth = Math.max(48, Math.round(resizingCol.startWidth + diff))
      setColWidths((prev) => ({ ...prev, [col.key]: newWidth }))
    }
    const onMouseUp = () => {
      const col = normalizedCols[resizingCol.colIndex]
      if (col) {
        const finalWidth = colWidths[col.key] || resizingCol.startWidth
        const updated = normalizedCols.map((c, i) =>
          i === resizingCol.colIndex ? { ...c, width: finalWidth } : c,
        )
        commit(rows, updated)
      }
      setResizingCol(null)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [resizingCol, normalizedCols, colWidths, commit, rows])

  // Row resizing mouse event listeners
  useEffect(() => {
    if (!resizingRow || typeof window === 'undefined') return
    const onMouseMove = (e: MouseEvent) => {
      const diff = e.clientY - resizingRow.startY
      const newHeight = Math.max(28, Math.round(resizingRow.startHeight + diff))
      setRowHeights((prev) => ({ ...prev, [resizingRow.rowId]: newHeight }))
    }
    const onMouseUp = () => {
      setResizingRow(null)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [resizingRow])

  // Close context menu on click outside
  useEffect(() => {
    if (!menu || typeof window === 'undefined') return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target?.closest?.('.free-sheet-context-menu')) {
        setMenu(null)
      }
    }
    window.addEventListener('mousedown', onMouseDown)
    return () => window.removeEventListener('mousedown', onMouseDown)
  }, [menu])

  // Cell change
  const handleCellChange = (visibleRowIdx: number, colIdx: number, rawVal: string) => {
    if (readOnly) return
    const col = normalizedCols[colIdx]
    const origIdx = visibleRows[visibleRowIdx]?.originalIndex
    if (!col || col.readOnly || col.key === 'line_total' || origIdx == null) return

    let valueToStore: string | number | null = rawVal
    if (col.type === 'number' || col.type === 'currency') {
      const parsed = parseNumberVi(rawVal)
      // Keep parsed number if valid; if invalid, keep raw text so user data is not lost
      valueToStore = parsed !== null ? parsed : rawVal
    }

    const nextRows = rows.map((row, idx) => {
      if (idx !== origIdx) return row
      const updated = { ...row, [col.key]: valueToStore }
      return computeRow ? computeRow(updated) : updated
    })
    commit(nextRows)
  }

  // Copy selection as TSV
  const copySelectionTsv = useCallback(() => {
    return copySelectionToTsv({
      rows,
      columns: normalizedCols,
      visibleRows,
      r0: rMin,
      r1: rMax,
      c0: cMin,
      c1: cMax,
      computeRow,
    })
  }, [computeRow, normalizedCols, rMax, rMin, rows, visibleRows, cMax, cMin])

  // Handle native onCopy
  const handleCopy = (e: ReactClipboardEvent) => {
    const tsv = copySelectionTsv()
    if (tsv) {
      e.clipboardData.setData('text/plain', tsv)
      e.preventDefault()
    }
  }

  // Handle Paste
  const handlePaste = (e: ReactClipboardEvent) => {
    if (readOnly) return
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    if (!text) return

    const nextRows = applyPasteToRows({
      tsv: text,
      rows,
      columns: normalizedCols,
      visibleRows,
      startR: selected.r,
      startC: selected.c,
      computeRow,
    })

    commit(nextRows)
  }

  // Keyboard navigation, Undo/Redo, Delete, Copy
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    // Header direct rename keyboard shortcuts
    if (selectedHeaderCol && !editingHeaderCol && !readOnly) {
      if (e.key === 'F2') {
        e.preventDefault()
        const col = normalizedCols.find((c) => c.key === selectedHeaderCol)
        if (col) {
          setEditingHeaderCol(col.key)
          setHeaderDraft(col.label)
        }
        return
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        setEditingHeaderCol(selectedHeaderCol)
        setHeaderDraft(e.key)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSelectedHeaderCol(null)
        return
      }
    }

    // Undo: Ctrl+Z / Cmd+Z
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault()
      const prev = history[history.length - 1]
      if (prev) {
        setHistory((h) => h.slice(0, -1))
        setFuture((f) => [{ columns: normalizedCols, rows }, ...f].slice(0, 50))
        onChange(prev)
      }
      return
    }

    // Redo: Ctrl+Y / Cmd+Y or Ctrl+Shift+Z
    if (
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey)
    ) {
      e.preventDefault()
      const next = future[0]
      if (next) {
        setFuture((f) => f.slice(1))
        setHistory((h) => [...h, { columns: normalizedCols, rows }])
        onChange(next)
      }
      return
    }

    // Copy: Ctrl+C / Cmd+C
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      const tsv = copySelectionTsv()
      if (tsv && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(tsv).catch(() => {})
      }
      return
    }

    // Delete cell range (skip core columns and readOnly)
    if (e.key === 'Delete' && !readOnly) {
      const tag = (e.target as HTMLElement).tagName
      const isRange = rMin !== rMax || cMin !== cMax
      if (tag === 'INPUT' && !isRange && selectedRows.size === 0) {
        return
      }

      // If whole rows were selected via STT column
      if (selectedRows.size > 0) {
        e.preventDefault()
        const origToRemove = new Set<number>()
        for (const vIdx of selectedRows) {
          if (visibleRows[vIdx]) origToRemove.add(visibleRows[vIdx].originalIndex)
        }
        const nextRows = rows.filter((_, idx) => !origToRemove.has(idx))
        setSelectedRows(new Set())
        commit(nextRows)
        return
      }

      // Cell selection delete: clears content of cells in range, ignoring readOnly and core columns
      e.preventDefault()
      const nextRows = clearCellsInSelection({
        rows,
        columns: normalizedCols,
        visibleRows,
        r0: rMin,
        r1: rMax,
        c0: cMin,
        c1: cMax,
      })

      commit(nextRows)
      return
    }

    // Arrow keys & Tab & Enter navigation
    if (['Tab', 'Enter', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault()
      let dr = 0
      let dc = 0

      if (e.key === 'Tab') {
        dc = e.shiftKey ? -1 : 1
      } else if (e.key === 'Enter') {
        dr = e.shiftKey ? -1 : 1
      } else if (e.key === 'ArrowRight') {
        dc = 1
      } else if (e.key === 'ArrowLeft') {
        dc = -1
      } else if (e.key === 'ArrowDown') {
        dr = 1
      } else if (e.key === 'ArrowUp') {
        dr = -1
      }

      const nextR = Math.max(0, Math.min(visibleRows.length - 1, selected.r + dr))
      const nextC = Math.max(0, Math.min(normalizedCols.length - 1, selected.c + dc))
      focusCell(nextR, nextC)
    }
  }

  // Context menu action dispatcher
  const handleMenuAction = (action: string) => {
    if (!menu || readOnly) return
    const { r, c } = menu
    const col = normalizedCols[c]

    if (action === 'rename-col' && col) {
      if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
        const promptVal = window.prompt('Nhập tên cột mới:', col.label)
        if (promptVal !== null && promptVal.trim() !== '') {
          commit(rows, renameColumn(normalizedCols, col.key, promptVal.trim()))
          setMenu(null)
          return
        }
      }
      setEditingHeaderCol(col.key)
      setHeaderDraft(col.label)
      setSelectedHeaderCol(col.key)
      setMenu(null)
      return
    }

    const res = dispatchMenuAction({
      action: action as never,
      rows,
      columns: normalizedCols,
      visibleRows,
      r,
      c,
    })

    commit(res.rows, res.columns)
    setMenu(null)
  }

  // Confirm rename dialog
  const handleConfirmRename = () => {
    if (renameModal && renameDraft.trim()) {
      commit(rows, renameColumn(normalizedCols, renameModal.key, renameDraft.trim()))
    }
    setRenameModal(null)
  }

  // Header sort toggle
  const handleSortToggle = (colKey: string) => {
    setSort((prev) => {
      const cur = prev.find((s) => s.key === colKey)
      if (!cur) return [{ key: colKey, direction: 'asc' }]
      if (cur.direction === 'asc') return [{ key: colKey, direction: 'desc' }]
      return []
    })
  }

  // Header filter popover trigger
  const handleOpenFilterPopover = (colKey: string, e: ReactMouseEvent) => {
    e.stopPropagation()
    if (activeFilterKey === colKey) {
      setActiveFilterKey(null)
      setFilterAnchorRect(null)
    } else {
      setActiveFilterKey(colKey)
      setFilterAnchorRect(e.currentTarget.getBoundingClientRect())
    }
  }

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilters({})
    setActiveFilterKey(null)
    setFilterAnchorRect(null)
  }

  // Row header STT click: full-row selection
  const handleSttClick = (r: number, e: ReactMouseEvent) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      setSelectedRows((prev) => {
        const next = new Set(prev)
        if (next.has(r)) next.delete(r)
        else next.add(r)
        return next
      })
    } else if (e.shiftKey && selectedRows.size > 0) {
      const minSelected = Math.min(...Array.from(selectedRows))
      const from = Math.min(minSelected, r)
      const to = Math.max(minSelected, r)
      const next = new Set<number>()
      for (let i = from; i <= to; i++) next.add(i)
      setSelectedRows(next)
    } else {
      setSelectedRows(new Set([r]))
    }
  }

  const isFiltering = Object.keys(filters).length > 0
  const activeColFilterObj = activeFilterKey
    ? normalizedCols.find((c) => c.key === activeFilterKey)
    : null

  // Footer sums
  const totalAll = totalKey ? sumColumn(rows, totalKey, computeRow) : null
  const totalFiltered =
    totalKey && isFiltering
      ? sumColumn(
          visibleRows.map((v) => v.row),
          totalKey,
          computeRow,
        )
      : null

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      data-testid="free-sheet-table"
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onCopy={handleCopy}
      className={`free-sheet-table-container relative flex flex-col border border-slate-300 rounded-[var(--radius-md)] bg-white text-xs font-sans outline-none focus:ring-1 focus:ring-sky-300 shadow-xs overflow-hidden ${className}`}
    >
      {/* Active Filter Banner */}
      {isFiltering && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-900 font-medium">
          <div className="flex items-center gap-1.5">
            <Filter className="h-3 w-3 text-amber-600" />
            <span>
              Đang lọc: hiện {visibleRows.length}/{rows.length} dòng
            </span>
          </div>
          <button
            type="button"
            onClick={handleClearAllFilters}
            className="text-amber-800 hover:text-amber-950 underline font-semibold cursor-pointer"
          >
            Xóa tất cả bộ lọc
          </button>
        </div>
      )}

      {/* Main Table Area */}
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full min-w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-100 text-slate-700 font-semibold select-none">
              {/* STT Header */}
              <th className="px-2.5 py-2 border-r border-slate-200 text-center w-12 min-w-12">
                STT
              </th>

              {/* Dynamic / Core Column Headers */}
              {normalizedCols.map((col, c) => {
                const isFiltered = isColumnFilterActive(col.key, filters, rows)
                const sortItem = sort.find((s) => s.key === col.key)
                const isDragOver = dragOverCol === c
                const isHeaderSelected = selectedHeaderCol === col.key
                const isHeaderEditing = editingHeaderCol === col.key
                const currentWidth = colWidths[col.key] ?? col.width

                return (
                  <th
                    key={col.key}
                    draggable={!readOnly && !isHeaderEditing && col.key !== 'note'}
                    onDragStart={() => {
                      draggedColIdx.current = c
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      if (draggedColIdx.current !== null && draggedColIdx.current !== c) {
                        setDragOverCol(c)
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverCol === c) setDragOverCol(null)
                    }}
                    onDrop={() => {
                      if (draggedColIdx.current !== null) {
                        commit(rows, moveColumn(normalizedCols, draggedColIdx.current, c))
                        draggedColIdx.current = null
                        setDragOverCol(null)
                      }
                    }}
                    style={{ width: currentWidth, minWidth: currentWidth }}
                    className={`relative px-2.5 py-2 border-r border-slate-200 last:border-r-0 transition-colors ${
                      isDragOver ? 'bg-sky-200 border-l-2 border-l-sky-600' : ''
                    } ${
                      isHeaderSelected && !isDragOver
                        ? 'bg-slate-200 text-slate-900 ring-1 ring-slate-400'
                        : 'bg-slate-100 text-slate-700'
                    } ${
                      col.type === 'number' || col.type === 'currency'
                        ? 'text-right'
                        : 'text-left'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      {isHeaderEditing ? (
                        <input
                          ref={headerInputRef}
                          type="text"
                          data-testid={`header-rename-input-${col.key}`}
                          value={headerDraft}
                          onChange={(e) => setHeaderDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              e.stopPropagation()
                              const nextLabel = headerDraft.trim() || col.label
                              commit(rows, renameColumn(normalizedCols, col.key, nextLabel))
                              setEditingHeaderCol(null)
                              setSelectedHeaderCol(null)
                            } else if (e.key === 'Escape') {
                              e.preventDefault()
                              e.stopPropagation()
                              setEditingHeaderCol(null)
                            }
                          }}
                          onBlur={() => {
                            const nextLabel = headerDraft.trim() || col.label
                            commit(rows, renameColumn(normalizedCols, col.key, nextLabel))
                            setEditingHeaderCol(null)
                            setSelectedHeaderCol(null)
                          }}
                          className="w-full rounded border border-slate-400 bg-white px-1.5 py-0.5 text-xs font-semibold text-slate-900 shadow-inner outline-none focus:ring-1 focus:ring-slate-500"
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            if (e.altKey || e.shiftKey) {
                              handleSortToggle(col.key)
                              return
                            }
                            setSelectedHeaderCol(col.key)
                            setSelectedRows(new Set())
                          }}
                          onDoubleClick={() => {
                            if (!readOnly) {
                              setEditingHeaderCol(col.key)
                              setHeaderDraft(col.label)
                              setSelectedHeaderCol(col.key)
                            }
                          }}
                          title={`${col.label} (Bấm để chọn, nhấp đúp hoặc F2 để sửa tên, Shift+Click để sắp xếp)`}
                          className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-slate-900 truncate cursor-pointer text-left flex-1"
                        >
                          <span className="truncate">{col.label}</span>
                          {sortItem && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSortToggle(col.key)
                              }}
                              className="text-sky-600 font-bold shrink-0 cursor-pointer"
                              title="Đổi chiều sắp xếp"
                            >
                              {sortItem.direction === 'asc' ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              )}
                            </span>
                          )}
                        </button>
                      )}

                      {/* Excel Filter Button */}
                      {!isHeaderEditing && (
                        <button
                          type="button"
                          onClick={(e) => handleOpenFilterPopover(col.key, e)}
                          aria-label={`Lọc ${col.label}`}
                          title={isFiltered ? 'Cột đang lọc' : `Lọc cột ${col.label}`}
                          className={`p-1 rounded transition-colors cursor-pointer shrink-0 ${
                            isFiltered
                              ? 'text-sky-600 bg-sky-100 hover:bg-sky-200 ring-1 ring-sky-300'
                              : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    {/* Column Resizer Handle */}
                    {!readOnly && !isHeaderEditing && (
                      <div
                        data-testid={`col-resizer-${col.key}`}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          const th = e.currentTarget.parentElement as HTMLElement
                          const startWidth = currentWidth || th?.getBoundingClientRect().width || 100
                          setResizingCol({ colIndex: c, startX: e.clientX, startWidth })
                        }}
                        onDoubleClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          let maxLen = col.label.length
                          for (const v of visibleRows) {
                            const val = v.computed[col.key]
                            const str = val != null ? String(val) : ''
                            if (str.length > maxLen) maxLen = str.length
                          }
                          const fitWidth = Math.min(600, Math.max(60, Math.round(maxLen * 9 + 40)))
                          const updated = normalizedCols.map((item, idx) =>
                            idx === c ? { ...item, width: fitWidth } : item,
                          )
                          setColWidths((prev) => ({ ...prev, [col.key]: fitWidth }))
                          commit(rows, updated)
                        }}
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-slate-400/60 transition-colors z-10 select-none"
                        title="Kéo mép để giãn cột, nhấp đúp để vừa nội dung"
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={normalizedCols.length + 1}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  Không có dòng dữ liệu nào.
                </td>
              </tr>
            ) : (
              visibleRows.map(({ row, computed, originalIndex }, r) => {
                const isRowSelected = selectedRows.has(r)
                const isDragOverRow = dragOverRow === r

                return (
                  <tr
                    key={row.id}
                    style={{ height: rowHeights[row.id] }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      if (draggedRowOrigIdx.current !== null) {
                        setDragOverRow(r)
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverRow === r) setDragOverRow(null)
                    }}
                    onDrop={() => {
                      if (draggedRowOrigIdx.current !== null) {
                        commit(moveRow(rows, draggedRowOrigIdx.current, originalIndex))
                        draggedRowOrigIdx.current = null
                        setDragOverRow(null)
                      }
                    }}
                    className={`transition-colors ${
                      isDragOverRow ? 'border-t-2 border-t-sky-500 bg-sky-50' : ''
                    } ${isRowSelected ? 'bg-amber-50' : 'hover:bg-slate-50/60'}`}
                  >
                    {/* STT Cell with row drag handle and row height resizer */}
                    <td
                      onClick={(e) => handleSttClick(r, e)}
                      className="relative px-2 py-1 text-center text-slate-500 border-r border-slate-200 select-none cursor-pointer"
                    >
                      <div className="flex items-center justify-center gap-1">
                        {!readOnly && (
                          <span
                            draggable
                            onDragStart={(e) => {
                              draggedRowOrigIdx.current = originalIndex
                              e.dataTransfer?.setData('text/plain', String(originalIndex))
                            }}
                            title="Kéo thả để đổi thứ tự dòng"
                            aria-label="Tay cầm di chuyển dòng"
                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
                          >
                            <GripVertical className="h-3 w-3" />
                          </span>
                        )}
                        <span>{r + 1}</span>
                      </div>
                      {!readOnly && (
                        <div
                          data-testid={`row-resizer-${row.id}`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const tr = e.currentTarget.closest('tr') as HTMLElement
                            const startHeight = rowHeights[row.id] || tr?.getBoundingClientRect().height || 34
                            setResizingRow({ rowId: row.id, startY: e.clientY, startHeight })
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setRowHeights((prev) => {
                              const next = { ...prev }
                              delete next[row.id]
                              return next
                            })
                          }}
                          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-row-resize hover:bg-slate-400/60 z-10 transition-colors select-none"
                          title="Kéo mép dưới để giãn chiều cao dòng, nhấp đúp để vừa nội dung"
                        />
                      )}
                    </td>

                    {/* Data Cells */}
                    {normalizedCols.map((col, c) => {
                      const val = computed[col.key]
                      const inRange = isInRange(r, c)
                      const isReadOnlyCell =
                        readOnly || col.readOnly || col.key === 'line_total'

                      const isNumCol = col.type === 'number' || col.type === 'currency'
                      const isInvalidNum =
                        isNumCol &&
                        val !== null &&
                        val !== undefined &&
                        val !== '' &&
                        typeof val === 'string' &&
                        parseNumberVi(val) === null

                      const isEditing = editingCell?.r === r && editingCell?.c === c
                      const parsedNum =
                        isNumCol && val !== null && val !== undefined && val !== ''
                          ? typeof val === 'number'
                            ? Number.isFinite(val)
                              ? val
                              : null
                            : parseNumberVi(val)
                          : null

                      const displayVal = isEditing
                        ? editingCell.draft
                        : col.type === 'currency'
                        ? parsedNum !== null
                          ? formatMoney(parsedNum)
                          : val != null
                          ? String(val)
                          : ''
                        : col.type === 'number'
                        ? parsedNum !== null
                          ? formatSheetAmount(parsedNum)
                          : val != null
                          ? String(val)
                          : ''
                        : val != null
                        ? String(val)
                        : ''

                      return (
                        <td
                          key={col.key}
                          onClick={(e) => {
                            setSelectedHeaderCol(null)
                            if (e.shiftKey) {
                              setSelected({ r, c })
                            } else {
                              setSelected({ r, c })
                              setAnchor({ r, c })
                            }
                            setSelectedRows(new Set())
                          }}
                          onMouseDown={(e) => {
                            if (e.button === 0 && !e.shiftKey) {
                              setSelectedHeaderCol(null)
                              setIsSelecting(true)
                              setSelected({ r, c })
                              setAnchor({ r, c })
                              setSelectedRows(new Set())
                            }
                          }}
                          onMouseEnter={() => {
                            if (isSelecting) {
                              setSelected({ r, c })
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault()
                            if (!readOnly) {
                              setSelectedHeaderCol(null)
                              setSelected({ r, c })
                              setAnchor({ r, c })
                              setMenu({
                                r,
                                c,
                                x: e.clientX,
                                y: e.clientY,
                              })
                            }
                          }}
                          className={`p-0 border-r border-slate-200 last:border-r-0 ${
                            inRange ? 'bg-sky-100/70' : ''
                          }`}
                        >
                          {isReadOnlyCell ? (
                            <div
                              title={displayVal}
                              className={`px-2 py-1.5 truncate text-slate-800 ${
                                col.type === 'number' || col.type === 'currency'
                                  ? 'text-right font-medium'
                                  : 'text-left'
                              }`}
                            >
                              {displayVal}
                            </div>
                          ) : (
                            <input
                              ref={(el) => {
                                inputRefs.current[`${r}_${c}`] = el
                              }}
                              type="text"
                              inputMode={isNumCol ? 'decimal' : 'text'}
                              aria-label={`${col.label} dòng ${r + 1}`}
                              value={displayVal}
                              onChange={(e) => {
                                const nextVal = e.target.value
                                setEditingCell({ r, c, draft: nextVal })
                                handleCellChange(r, c, nextVal)
                              }}
                              onFocus={() => {
                                setSelected({ r, c })
                                setAnchor({ r, c })
                                setSelectedHeaderCol(null)
                                const currentDraft = val != null ? String(val) : ''
                                setEditingCell({ r, c, draft: currentDraft })
                              }}
                              onBlur={() => {
                                setEditingCell(null)
                              }}
                              onWheel={(e) => (e.target as HTMLElement).blur()}
                              className={`w-full h-full px-2 py-1.5 bg-transparent border-0 outline-none truncate transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                isNumCol ? 'text-right' : 'text-left'
                              } ${
                                isInvalidNum
                                  ? 'ring-1 ring-amber-400 border-amber-400 bg-amber-50/50'
                                  : ''
                              }`}
                            />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Totals */}
      {!hideFooter && totalKey && totalAll !== null && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800">
          <div className="text-slate-500 font-normal">
            {isFiltering && totalFiltered !== null && (
              <span>
                Tổng đang lọc: <strong className="text-sky-700">{formatMoney(totalFiltered)}</strong>
              </span>
            )}
          </div>
          <div className="text-right">
            <span>TỔNG CỘNG: </span>
            <span className="text-sm font-bold text-slate-900">{formatMoney(totalAll)}</span>
          </div>
        </div>
      )}

      {/* Reused Excel Filter Popover */}
      {activeFilterKey && activeColFilterObj && filterAnchorRect && (
        <ExcelFilterPopover
          column={activeColFilterObj as never}
          data={rows as never}
          filterItem={filters[activeFilterKey]}
          anchorRect={filterAnchorRect}
          onApply={(item) => {
            if (item) {
              setFilters((prev) => ({ ...prev, [activeFilterKey]: item }))
            } else {
              setFilters((prev) => {
                const next = { ...prev }
                delete next[activeFilterKey]
                return next
              })
            }
            setActiveFilterKey(null)
            setFilterAnchorRect(null)
          }}
          onClose={() => {
            setActiveFilterKey(null)
            setFilterAnchorRect(null)
          }}
        />
      )}

      {/* Context Menu (Right Click) */}
      {menu && !readOnly && (
        <div
          style={{ left: menu.x, top: menu.y }}
          className="free-sheet-context-menu fixed z-50 min-w-44 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10 border border-slate-200 text-xs font-sans"
        >
          {/* Column actions */}
          <button
            type="button"
            onClick={() => handleMenuAction('insert-col-left')}
            className="flex w-full items-center px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50 cursor-pointer"
          >
            Chèn cột bên trái
          </button>
          <button
            type="button"
            disabled={normalizedCols[menu.c]?.key === 'note'}
            onClick={() => handleMenuAction('insert-col-right')}
            className={`flex w-full items-center px-3 py-1.5 text-left ${
              normalizedCols[menu.c]?.key === 'note'
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-700 hover:bg-slate-50 cursor-pointer'
            }`}
          >
            Chèn cột bên phải
          </button>
          <button
            type="button"
            disabled={
              normalizedCols[menu.c]?.core ||
              isCoreColumn(normalizedCols[menu.c]?.key)
            }
            onClick={() => handleMenuAction('rename-col')}
            className={`flex w-full items-center px-3 py-1.5 text-left ${
              normalizedCols[menu.c]?.core ||
              isCoreColumn(normalizedCols[menu.c]?.key)
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-700 hover:bg-slate-50 cursor-pointer'
            }`}
          >
            Đổi tên cột
          </button>
          <button
            type="button"
            disabled={
              normalizedCols[menu.c]?.core ||
              isCoreColumn(normalizedCols[menu.c]?.key)
            }
            onClick={() => handleMenuAction('delete-col')}
            className={`flex w-full items-center px-3 py-1.5 text-left ${
              normalizedCols[menu.c]?.core ||
              isCoreColumn(normalizedCols[menu.c]?.key)
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-red-600 hover:bg-red-50 cursor-pointer'
            }`}
          >
            Xóa cột
          </button>

          <div className="my-1 border-t border-slate-100" />

          {/* Row actions */}
          <button
            type="button"
            onClick={() => handleMenuAction('insert-row-above')}
            className="flex w-full items-center px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50 cursor-pointer"
          >
            Chèn dòng phía trên
          </button>
          <button
            type="button"
            onClick={() => handleMenuAction('insert-row-below')}
            className="flex w-full items-center px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50 cursor-pointer"
          >
            Chèn dòng phía dưới
          </button>
          <button
            type="button"
            disabled={menu.r <= 0}
            onClick={() => handleMenuAction('move-row-up')}
            className={`flex w-full items-center px-3 py-1.5 text-left ${
              menu.r <= 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-700 hover:bg-slate-50 cursor-pointer'
            }`}
          >
            Chuyển dòng lên
          </button>
          <button
            type="button"
            disabled={menu.r >= visibleRows.length - 1}
            onClick={() => handleMenuAction('move-row-down')}
            className={`flex w-full items-center px-3 py-1.5 text-left ${
              menu.r >= visibleRows.length - 1
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-700 hover:bg-slate-50 cursor-pointer'
            }`}
          >
            Chuyển dòng xuống
          </button>
          <button
            type="button"
            onClick={() => handleMenuAction('delete-row')}
            className="flex w-full items-center px-3 py-1.5 text-left text-red-600 hover:bg-red-50 cursor-pointer"
          >
            Xóa dòng
          </button>
        </div>
      )}

      {/* Rename Column Modal */}
      {renameModal && (
        <div
          role="dialog"
          aria-label="Đổi tên cột"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
        >
          <div className="bg-white border border-slate-300 rounded-md p-4 shadow-xl w-72 text-xs">
            <div className="font-semibold text-slate-800 mb-2">Đổi tên cột</div>
            <input
              aria-label="Tên cột mới"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded bg-white text-xs mb-3 focus:outline-none focus:border-sky-500"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRenameModal(null)}
                className="px-2.5 py-1 border border-slate-300 rounded hover:bg-slate-100 text-slate-700 cursor-pointer"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                className="px-3 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded font-medium cursor-pointer"
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
